import { forwardRef } from 'react'

import { tv } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

export type ChipVariant = 'filled' | 'outlined'
export type ChipSize = 'xs' | 'sm' | 'md' | 'lg'
export type ChipIconPosition = 'left' | 'right'
export type ChipStatus = 'default' | 'hover'

const iconSizes: Record<ChipSize, number> = {
  xs: 18,
  sm: 18,
  md: 21,
  lg: 24
}

const cancelIconPath =
  'M12 13.0537L15.073 16.127C15.2115 16.2653 15.3856 16.3362 15.5952 16.3395C15.8047 16.3427 15.982 16.2718 16.127 16.127C16.2718 15.982 16.3443 15.8063 16.3443 15.6C16.3443 15.3937 16.2718 15.218 16.127 15.073L13.0538 12L16.127 8.927C16.2653 8.7885 16.3362 8.61442 16.3395 8.40475C16.3427 8.19525 16.2718 8.018 16.127 7.873C15.982 7.72817 15.8063 7.65575 15.6 7.65575C15.3937 7.65575 15.218 7.72817 15.073 7.873L12 10.9462L8.927 7.873C8.7885 7.73467 8.61442 7.66383 8.40475 7.6605C8.19525 7.65733 8.018 7.72817 7.873 7.873C7.72817 8.018 7.65575 8.19367 7.65575 8.4C7.65575 8.60633 7.72817 8.782 7.873 8.927L10.9462 12L7.873 15.073C7.73467 15.2115 7.66383 15.3856 7.6605 15.5952C7.65733 15.8047 7.72817 15.982 7.873 16.127C8.018 16.2718 8.19367 16.3443 8.4 16.3443C8.60633 16.3443 8.782 16.2718 8.927 16.127L12 13.0537ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9982 2.5C13.3122 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9982C21.5 13.3122 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z'

const chipVariants = tv(
  {
    slots: {
      root: 'font-body group/chip inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-[72px]',
      label: 'shrink-0 font-normal group-hover/chip:font-semibold',
      icon: '[&>*]:text-[length:inherit]/none flex shrink-0 items-center justify-center [&>*]:block [&>*]:size-full'
    },
    variants: {
      variant: {
        filled: {
          root: 'bg-primary-300 hover:bg-primary data-[status=hover]:bg-primary',
          label: 'text-white'
        },
        outlined: {
          root: 'border-surface-400 bg-surface hover:border-surface-300 hover:bg-surface-200 data-[status=hover]:border-surface-300 data-[status=hover]:bg-surface-200 border border-dashed',
          label: 'text-surface-title-50'
        }
      },
      size: {
        xs: {
          label: 'text-control-xs font-semibold',
          icon: 'size-[18px] text-[18px]/none'
        },
        sm: {
          label: 'text-control-sm',
          icon: 'size-[18px] text-[18px]/none'
        },
        md: {
          label: 'text-control-md',
          icon: 'size-[21px] text-[21px]/none'
        },
        lg: {
          label: 'text-control-lg',
          icon: 'size-6 text-2xl/none'
        }
      },
      status: {
        default: {},
        hover: {
          label: 'font-semibold'
        }
      }
    },
    // Figma draws the outlined stroke inside the chip bounds.
    compoundVariants: [
      { variant: 'filled', size: 'xs', class: { root: 'px-2 py-0.5' } },
      { variant: 'filled', size: 'sm', class: { root: 'px-4 py-1' } },
      { variant: 'filled', size: 'md', class: { root: 'px-6 py-2' } },
      { variant: 'filled', size: 'lg', class: { root: 'px-6 py-2' } },
      { variant: 'outlined', size: 'xs', class: { root: 'px-[7px] py-[1px]' } },
      {
        variant: 'outlined',
        size: 'sm',
        class: { root: 'px-[15px] py-[3px]' }
      },
      {
        variant: 'outlined',
        size: 'md',
        class: { root: 'px-[23px] py-[7px]' }
      },
      { variant: 'outlined', size: 'lg', class: { root: 'px-[23px] py-[7px]' } }
    ],
    defaultVariants: {
      variant: 'filled',
      size: 'lg',
      status: 'default'
    }
  },
  { twMerge: false }
)

export interface ChipProps
  extends Omit<React.ComponentPropsWithRef<'span'>, 'children'> {
  children: React.ReactNode
  variant?: ChipVariant
  size?: ChipSize
  status?: ChipStatus
  iconPosition?: ChipIconPosition
  /** Decorative icon. Defaults to the cancel glyph when iconPosition is set. */
  icon?: React.ReactNode
  /** Additional decorative icon after the label, preserving the leading icon. */
  trailingIcon?: React.ReactNode
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  (
    {
      children,
      variant = 'filled',
      size = 'lg',
      status = 'default',
      iconPosition,
      icon,
      trailingIcon,
      className,
      ...props
    },
    ref
  ) => {
    const {
      root,
      label,
      icon: iconClassName
    } = chipVariants({
      variant,
      size,
      status
    })
    const decoration =
      iconPosition && icon ? (
        <span
          data-slot='chip-icon'
          aria-hidden='true'
          className={cn(
            iconClassName(),
            variant === 'filled' ? 'text-white' : 'text-surface-title-50'
          )}
        >
          {icon}
        </span>
      ) : iconPosition ? (
        <svg
          width={iconSizes[size]}
          height={iconSizes[size]}
          viewBox='0 0 24 24'
          fill='none'
          aria-hidden='true'
          focusable='false'
          data-slot='chip-icon'
          className={cn(
            'block shrink-0',
            variant === 'filled' ? 'text-white' : 'text-surface-title-50'
          )}
        >
          <path d={cancelIconPath} fill='currentColor' />
        </svg>
      ) : null

    return (
      <span
        ref={ref}
        data-slot='chip'
        data-variant={variant}
        data-size={size}
        data-status={status}
        data-icon-position={iconPosition}
        className={cn(root(), className)}
        {...props}
      >
        {iconPosition === 'left' && decoration}
        <span data-slot='chip-label' className={cn(label())}>
          {children}
        </span>
        {iconPosition === 'right' && decoration}
        {trailingIcon && (
          <span
            data-slot='chip-trailing-icon'
            aria-hidden='true'
            className={cn(
              'block shrink-0',
              variant === 'filled' ? 'text-white' : 'text-surface-title-50'
            )}
          >
            {trailingIcon}
          </span>
        )}
      </span>
    )
  }
)
Chip.displayName = 'Chip'
